import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { useUserStore } from './user.store'

type OnboardingArea = 'dashboard' | 'settings'

// SIB-924: wizard completion belongs to an account, not to the browser. The
// persisted state is therefore keyed by the signed-in user id, so a different
// account logging in on the same machine cannot inherit another account's
// completion state. Without a signed-in user the plain key is used (nothing
// user-specific is written there in practice — the wizard only runs while
// signed in).
const scopedName = (name: string) => {
  const userId = useUserStore.getState().user?.id
  return userId ? `${name}:${userId}` : name
}

const userScopedStorage = createJSONStorage<Partial<OnboardingState>>(() => ({
  getItem: name => localStorage.getItem(scopedName(name)),
  setItem: (name, value) => localStorage.setItem(scopedName(name), value),
  removeItem: name => localStorage.removeItem(scopedName(name)),
}))

export type OnboardingState = {
  isActive: boolean
  currentStep: number

  // ✅ separate completions
  completed: Record<OnboardingArea, boolean>

  // optional: can be used to “force” settings after dashboard
  shouldStartSettingsAfterDashboard: boolean

  startWizard: () => void
  stopWizard: () => void
  completeWizard: (area: OnboardingArea) => void
  setStep: (step: number) => void
  resetWizard: (area?: OnboardingArea) => void
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    set => ({
      isActive: false,
      currentStep: 0,

      completed: {
        dashboard: false,
        settings: false,
      },

      shouldStartSettingsAfterDashboard: false,

      startWizard: () => set({ isActive: true }),

      stopWizard: () => set({ isActive: false }),

      // ✅ mark only that area completed
      completeWizard: area =>
        set(state => ({
          isActive: false,
          completed: { ...state.completed, [area]: true },
          currentStep: 0,
          shouldStartSettingsAfterDashboard: area === 'dashboard' ? true : false,
        })),

      setStep: step => set({ currentStep: step }),

      // ✅ reset either one or all
      resetWizard: (area?: OnboardingArea) =>
        set(state => {
          if (!area) {
            return {
              isActive: false,
              currentStep: 0,
              completed: { dashboard: false, settings: false },
              shouldStartSettingsAfterDashboard: false,
            }
          }
          return {
            isActive: false,
            currentStep: 0,
            completed: { ...state.completed, [area]: false },
            shouldStartSettingsAfterDashboard: false,
          }
        }),
    }),
    {
      name: 'onboarding-store',
      storage: userScopedStorage,
      partialize: state => ({
        completed: state.completed,
        shouldStartSettingsAfterDashboard: state.shouldStartSettingsAfterDashboard,
      }),
    }
  )
)

// When the signed-in identity changes, swap to that account's persisted
// completion in a single setState: defaults when the account has none (so it
// cannot inherit the previous account's state), the stored values otherwise.
// The value is read directly rather than via persist.rehydrate(), because a
// reset-then-rehydrate would first persist the defaults over the returning
// account's saved completion.
let lastOnboardingUserId: string | null = null
useUserStore.subscribe(state => {
  const userId = state.user?.id ?? null
  if (userId === lastOnboardingUserId) return
  lastOnboardingUserId = userId

  let persisted: Partial<
    Pick<OnboardingState, 'completed' | 'shouldStartSettingsAfterDashboard'>
  > | null = null
  if (userId) {
    try {
      const raw = localStorage.getItem(`onboarding-store:${userId}`)
      persisted = raw ? (JSON.parse(raw)?.state ?? null) : null
    } catch {
      persisted = null
    }
  }

  useOnboardingStore.setState({
    isActive: false,
    currentStep: 0,
    completed: persisted?.completed ?? { dashboard: false, settings: false },
    shouldStartSettingsAfterDashboard: persisted?.shouldStartSettingsAfterDashboard ?? false,
  })
})
